import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useMemo, useState, type SyntheticEvent } from "react";

import { publicEnv } from "../app/env";
import {
  BILLBOARD_CADENCE_LABELS,
  billboardDestinationDomain,
  billboardDestinationError,
  billboardGraphemeCount,
  billboardMessageError,
  currentBillboardPriceCents,
  nextBillboardDecayAt,
} from "../features/billboards/billboardDisplay";
import type {
  BillboardQuote,
  BillboardReportReason,
} from "../services/billboard-api/BillboardApi";
import { useBillboardStore } from "../stores/useBillboardStore";
import { ModalShell } from "./ModalShell";

const stripePromise = publicEnv.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(publicEnv.VITE_STRIPE_PUBLISHABLE_KEY)
  : null;

function money(cents: number, currency = "usd"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function duration(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours}h ${String(minutes).padStart(2, "0")}m`
    : `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

function placementDisclosure(slotId: "hourly" | "daily" | "forever"): string {
  if (slotId === "forever") {
    return "Forever means the price never decays—not permanent ownership. Another driver can replace this ad immediately.";
  }
  const period = slotId === "hourly" ? "1 hour" : "1 day";
  return `Your paid placement may last less than ${period}: another driver can replace it immediately. If nobody does, it stays live while the takeover price begins to drop after ${period}.`;
}

function StripePaymentForm({ quote }: { quote: BillboardQuote }) {
  const stripe = useStripe();
  const elements = useElements();
  const paymentConfirmed = useBillboardStore((state) => state.paymentConfirmed);
  const setPaymentError = useBillboardStore((state) => state.setPaymentError);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    const result = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (result.error) {
      setPaymentError(
        result.error.message ?? "Payment authorization was not completed.",
      );
      setSubmitting(false);
      return;
    }
    await paymentConfirmed();
  };

  return (
    <form
      className="billboard-payment"
      onSubmit={(event) => void submit(event)}
    >
      <PaymentElement />
      <button
        className="button button--primary"
        type="submit"
        disabled={!stripe || !elements || submitting}
      >
        {submitting
          ? "AUTHORIZING…"
          : `AUTHORIZE ${money(quote.amountCents, quote.currency)}`}
      </button>
      <small>
        Your payment method is authorized first. It is captured only if this
        slot is still available.
      </small>
    </form>
  );
}

export function BillboardModal() {
  const modalOpen = useBillboardStore((state) => state.modalOpen);
  const selectedSlotId = useBillboardStore((state) => state.selectedSlotId);
  const slots = useBillboardStore((state) => state.slots);
  const serverOffsetMs = useBillboardStore((state) => state.serverOffsetMs);
  const mode = useBillboardStore((state) => state.mode);
  const quote = useBillboardStore((state) => state.quote);
  const purchaseState = useBillboardStore((state) => state.purchaseState);
  const resultMessage = useBillboardStore((state) => state.resultMessage);
  const reportMessage = useBillboardStore((state) => state.reportMessage);
  const close = useBillboardStore((state) => state.close);
  const resetPurchase = useBillboardStore((state) => state.resetPurchase);
  const requestQuote = useBillboardStore((state) => state.requestQuote);
  const report = useBillboardStore((state) => state.report);
  const [message, setMessage] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [now, setNow] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportDetails, setReportDetails] = useState("");

  const slot = slots.find((candidate) => candidate.id === selectedSlotId);
  const placement = slot?.placement ?? null;
  const domain = billboardDestinationDomain(placement?.destinationUrl ?? null);

  useEffect(() => {
    if (!modalOpen) return;
    const initialTimer = window.setTimeout(() => setNow(Date.now()), 0);
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => {
      window.clearTimeout(initialTimer);
      window.clearInterval(timer);
    };
  }, [modalOpen]);

  const closeModal = () => {
    setMessage("");
    setDestinationUrl("");
    setAcknowledged(false);
    setReportOpen(false);
    setReportDetails("");
    close();
  };

  const messageError = billboardMessageError(message);
  const destinationError = billboardDestinationError(destinationUrl);
  const price = slot
    ? currentBillboardPriceCents(slot, serverOffsetMs, now)
    : null;
  const nextDecay = slot ? nextBillboardDecayAt(slot, serverOffsetMs) : null;
  const canQuote =
    slot &&
    !messageError &&
    !destinationError &&
    acknowledged &&
    purchaseState === "editing";
  const elementsOptions = useMemo(
    () =>
      quote
        ? {
            clientSecret: quote.clientSecret,
            appearance: {
              theme: "night" as const,
              variables: {
                colorPrimary: "#ff5a1f",
                colorBackground: "#0d1013",
                colorText: "#f4f1e8",
                borderRadius: "3px",
              },
            },
          }
        : null,
    [quote],
  );

  if (!modalOpen || !selectedSlotId) return null;

  const sendReport = (reason: BillboardReportReason) => {
    if (!placement) return;
    void report({
      placementId: placement.id,
      reason,
      ...(reportDetails.trim() ? { details: reportDetails.trim() } : {}),
    });
    setReportOpen(false);
  };

  return (
    <ModalShell
      title={`${BILLBOARD_CADENCE_LABELS[selectedSlotId]} billboard`}
      eyebrow="BILLBOARD TAKEOVER"
      headerBadge={mode === "mock" ? "DEMO" : "PAID AD"}
      className="billboard-modal"
      onClose={closeModal}
    >
      {!slot ? (
        <p className="billboard-status" role="status">
          Loading this billboard…
        </p>
      ) : (
        <>
          <section className="billboard-current" aria-label="Current billboard">
            <div>
              <span>ON THE ROAD NOW</span>
              <strong>{placement?.message ?? "System house ad"}</strong>
              <small>
                {domain ? `Sponsored link · ${domain}` : "No outbound link"}
              </small>
            </div>
            {placement?.destinationUrl ? (
              <a
                href={placement.destinationUrl}
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
              >
                Visit sponsor
              </a>
            ) : null}
          </section>

          {placement ? (
            <div className="billboard-report">
              {reportOpen ? (
                <>
                  <label>
                    <span>Optional report details</span>
                    <input
                      value={reportDetails}
                      maxLength={500}
                      onChange={(event) => setReportDetails(event.target.value)}
                    />
                  </label>
                  <div>
                    {(
                      [
                        ["offensive", "Offensive"],
                        ["misleading", "Misleading"],
                        ["malware", "Malware"],
                        ["other", "Other"],
                      ] as const
                    ).map(([reason, label]) => (
                      <button
                        key={reason}
                        type="button"
                        onClick={() => sendReport(reason)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <button type="button" onClick={() => setReportOpen(true)}>
                  Report this ad
                </button>
              )}
              {reportMessage ? (
                <small role="status">{reportMessage}</small>
              ) : null}
            </div>
          ) : null}

          <div className="billboard-price">
            <span>EXACT TAKEOVER PRICE</span>
            <strong>{money(price ?? slot.currentPriceCents)}</strong>
            <small>
              {nextDecay
                ? `Drops by $1 every ${slot.id === "hourly" ? "hour" : "day"} · next drop in ${duration(nextDecay - now)}`
                : "Permanent slot · this price does not decay"}
            </small>
          </div>
          <p className="billboard-run-disclosure">
            <strong>NO GUARANTEED RUN TIME</strong>
            <span>{placementDisclosure(slot.id)}</span>
          </p>

          <div className="billboard-compose">
            <label>
              <span>
                Message
                <small>{billboardGraphemeCount(message.trim())}/48</small>
              </span>
              <textarea
                rows={2}
                value={message}
                placeholder={"MAKE TRAFFIC\nMAKE SENSE"}
                disabled={purchaseState !== "editing"}
                aria-invalid={Boolean(message && messageError)}
                onChange={(event) => setMessage(event.target.value)}
              />
              {message && messageError ? (
                <small className="field-error">{messageError}</small>
              ) : null}
            </label>
            <label>
              <span>Optional HTTPS destination</span>
              <input
                type="url"
                inputMode="url"
                value={destinationUrl}
                placeholder="https://example.com"
                disabled={purchaseState !== "editing"}
                aria-invalid={Boolean(destinationError)}
                onChange={(event) => setDestinationUrl(event.target.value)}
              />
              {destinationError ? (
                <small className="field-error">{destinationError}</small>
              ) : null}
            </label>
          </div>

          <section className="billboard-preview" aria-label="Billboard preview">
            <header>
              <span>PAID AD</span>
              <span>{BILLBOARD_CADENCE_LABELS[selectedSlotId]}</span>
            </header>
            <strong>{message.trim() || "YOUR MESSAGE HERE"}</strong>
            <small>
              {billboardDestinationDomain(destinationUrl) ?? "NO LINK"}
            </small>
          </section>

          {purchaseState === "editing" || purchaseState === "creating" ? (
            <>
              <label className="billboard-acknowledgement">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />
                <span>
                  I understand another buyer may replace this ad immediately,
                  and moderation may void it without refund.
                </span>
              </label>
              <button
                className="button button--primary billboard-takeover-button"
                type="button"
                disabled={!canQuote}
                onClick={() =>
                  void requestQuote({
                    slotId: selectedSlotId,
                    message: message.trim(),
                    ...(destinationUrl.trim()
                      ? { destinationUrl: destinationUrl.trim() }
                      : {}),
                  })
                }
              >
                {purchaseState === "creating"
                  ? "RESERVING…"
                  : mode === "mock"
                    ? `TAKE OVER NOW · DEMO`
                    : `CONTINUE TO PAYMENT · ${money(price ?? slot.currentPriceCents)}`}
              </button>
            </>
          ) : null}

          {purchaseState === "payment" && quote ? (
            stripePromise && elementsOptions ? (
              <Elements stripe={stripePromise} options={elementsOptions}>
                <StripePaymentForm quote={quote} />
              </Elements>
            ) : (
              <div className="billboard-result billboard-result--failure">
                Stripe payments are not configured for this environment.
              </div>
            )
          ) : null}

          {purchaseState === "processing" ? (
            <div className="billboard-result" role="status">
              <span className="live-dot" aria-hidden="true" />
              {resultMessage ?? "Activating your billboard…"}
            </div>
          ) : null}

          {["success", "stale", "failure"].includes(purchaseState) ? (
            <div
              className={`billboard-result billboard-result--${purchaseState}`}
              role="status"
            >
              <strong>
                {purchaseState === "success"
                  ? "TAKEOVER LIVE"
                  : purchaseState === "stale"
                    ? "SLOT CHANGED"
                    : "TAKEOVER NOT COMPLETED"}
              </strong>
              <span>{resultMessage}</span>
              {purchaseState !== "success" ? (
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={resetPurchase}
                >
                  Try again
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </ModalShell>
  );
}
